import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { MutationFunction } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useToast } from "@/components/ui/use-toast";
import { Module } from "@/constants";
import {
  StoreProjectAssigneeListItem,
  StoreProjectAssigneeUpdate,
  StoreProjectAssigneeUpdateResponse,
} from "@/types/storeProjectAssignees";
import { ApiError } from "@/services/api";
import { StoreProjectAssigneesService } from "@/services/storeProjectAssignees";
import { useStoreProjectAssigneeId } from "@/modules/storeProjectAssignees/hooks/useStoreProjectAssigneeId";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";

export const useUpdateStoreProjectAssignee = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const id = useStoreProjectAssigneeId();
  const { invalidateListQuery } = useQueryCacheUpdater(
    Module.StoreProjectAssignees,
  );

  const mutationFn: MutationFunction<
    StoreProjectAssigneeUpdateResponse,
    StoreProjectAssigneeUpdate
  > = async (values) => {
    const response = await StoreProjectAssigneesService.update(id, values);
    return response;
  };

  const mutation = useMutationWithErrorHandling<
    StoreProjectAssigneeListItem,
    ApiError,
    StoreProjectAssigneeUpdate,
    unknown
  >({
    mutationFn,
    onSuccess: () => {
      toast({
        title: t("storeProjectAssignees.success.update"),
      });
      invalidateListQuery();
    },
    errorTitle: t("storeProjectAssignees.errors.update"),
  });

  return mutation;
};
